import type { Object3D } from 'three';
import { Vector3 } from 'three';
import { GuardFigure } from '../world/guard-figure';
import { ThiefFigure } from '../world/thief-figure';
import { Btn, PFlag, makeCommand, type Command, type NetPlayer } from '../net/protocol';
import type { Team } from '../net/protocol';
import type { Vec3 } from '../sim/types';
import type { MallPhysics } from './physics';
import { Player, type PlayerFrame } from './player';

/**
 * THE OTHER PEOPLE, AS STATE.
 *
 * One `Actor` per person in the round, on every machine, whatever their role.
 * What differs between machines is which of an actor's three parts exist:
 *
 *   body    a physics capsule the character controller drives. The host has one
 *           for everybody, because the host is the only machine whose answer to
 *           "did that thief get through the doorway" counts. A client has one
 *           only for itself, so it can predict its own movement and feel
 *           instant; every other capsule on a client would be a capsule
 *           arguing with the host about a collision it cannot see.
 *   figure  a visible body. Everyone except your own first-person view.
 *   buf     received transforms, on a client, for the people it does not
 *           simulate. Played back 100ms in the past — see `INTERP_MS`.
 *
 * That split is the whole of the netcode's shape, and it is why this file exists
 * separately from the session: the session should not be full of `if (isHost)`.
 *
 * Solo play runs through here too, with exactly one actor that has a body, no
 * figure and an empty buffer. Single player is not a special case with the
 * network bolted on later; it is a one-person lobby, which is the only way this
 * code gets exercised enough to be trustworthy.
 */

/**
 * How far in the past remote bodies are drawn, in milliseconds.
 *
 * Two snapshots at 20Hz is 100ms, which is the smallest buffer that survives a
 * late packet without the body stopping dead and then teleporting. It is also
 * the cost: a thief you see is a tenth of a second stale, which in a game about
 * hiding from a torch beam is a fair trade and in a shooter would not be.
 */
const INTERP_MS = 100;

/** Older than this and the buffer is a liability, not a history. */
const BUF_MS = 1000;

/**
 * Above this error the client stops easing and teleports.
 *
 * A metre and a half is roughly "you went through a different side of that
 * pillar than the host thinks you did". Below it, sliding the error away is
 * invisible; above it, sliding looks like being dragged and the player would
 * rather be told the truth abruptly.
 */
const SNAP_ERROR_M = 1.5;

/** How fast a small prediction error is dissolved, per second. */
const CORRECT_RATE = 10;

interface Sample {
  /** Local clock, ms, when this arrived. Not the host's clock — see push(). */
  t: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  flags: number;
  kg: number;
}

export interface ActorInit {
  id: string;
  slot: number;
  name: string;
  team: Team;
  /** This machine's own player. Gets no figure and owns the camera. */
  local: boolean;
  /** Give it a capsule. True on the host for everyone, on a client for itself. */
  simulated: boolean;
  spawn: Vec3;
  /** Where a figure is parented. Kept out of the static batch. */
  stage: Object3D;
  physics: MallPhysics;
}

export class Actor {
  readonly id: string;
  readonly slot: number;
  readonly team: Team;
  readonly local: boolean;
  name: string;

  /** Non-null exactly when this machine simulates them. */
  body: Player | null = null;
  figure: ThiefFigure | GuardFigure | null = null;

  /** Host: commands from this player waiting to be stepped. */
  readonly queue: Command[] = [];
  /** The last command consumed. Repeated when the queue starves. */
  readonly last: Command = makeCommand();
  /** Highest command sequence this machine has actually simulated. */
  ack = 0;

  /** Host: what this actor is trying to interact with, as they reported it. */
  wants: string | null = null;

  /** Last frame produced by the body, or reconstructed from the wire. */
  frame: PlayerFrame = { speed: 0, grounded: true, crouching: false, sprinting: false, stamina: 1, travelled: 0 };

  /** Where the world should draw them, whatever the source. */
  readonly pos = new Vector3();
  yaw = 0;
  pitch = 0;
  kg = 0;
  down = 0;

  /** Client: the interpolation buffer, oldest first. */
  private readonly buf: Sample[] = [];
  /** Client: the accumulated prediction error still being eased away. */
  private readonly error = new Vector3();
  private prev = new Vector3();

  constructor(init: ActorInit) {
    this.id = init.id;
    this.slot = init.slot;
    this.team = init.team;
    this.local = init.local;
    this.name = init.name;
    this.pos.set(init.spawn.x, init.spawn.y, init.spawn.z);
    this.prev.copy(this.pos);

    if (init.simulated) this.body = new Player(init.physics, init.spawn);
    if (!init.local) {
      this.figure =
        init.team === 'guard' ? new GuardFigure(`crew-${init.id}`) : new ThiefFigure(init.id, init.slot);
      if (this.figure instanceof GuardFigure) this.figure.torchOn(true);
      init.stage.add(this.figure.root);
    }
  }

  /** Host: take one command off the queue, or repeat the last one. */
  nextCommand(): Command {
    const c = this.queue.shift();
    if (c) {
      copyCmd(c, this.last);
      this.ack = c.seq;
      return this.last;
    }
    // A starved queue means a late packet, and the least wrong thing to do is
    // keep walking. Jump is cleared because a held-down repeat would turn one
    // dropped packet into a pogo stick, and it is the only button whose repeat
    // is not what the player meant.
    this.last.buttons &= ~Btn.Jump;
    return this.last;
  }

  /**
   * Host: accept commands from the wire.
   *
   * Anything at or below what we have already simulated is a duplicate from a
   * retransmit and is dropped. The cap is a flood guard: a client that sends a
   * thousand commands cannot make the host spend a thousand ticks on them.
   */
  enqueue(cmds: readonly Command[]): void {
    for (const c of cmds) {
      if (seqNewer(c.seq, this.ack) && (!this.queue.length || seqNewer(c.seq, this.queue[this.queue.length - 1].seq))) {
        this.queue.push({ ...c });
      }
    }
    if (this.queue.length > 32) this.queue.splice(0, this.queue.length - 32);
  }

  /** Client: a transform for someone we do not simulate. */
  push(p: NetPlayer, now: number): void {
    // Stamped with local arrival rather than the host's tick. The two clocks are
    // not synchronised and never will be; what playback actually needs is
    // "spaced the way they arrived", and arrival time says that directly.
    this.buf.push({ t: now, x: p.x, y: p.y, z: p.z, yaw: p.yaw, pitch: p.pitch, flags: p.flags, kg: p.kg });
    while (this.buf.length > 2 && now - this.buf[0].t > BUF_MS) this.buf.shift();
  }

  /**
   * Client: reconcile our own predicted position against the host's.
   *
   * Deliberately not rewind-and-replay. Replaying the last N commands would mean
   * re-running them against every other capsule where it stands NOW rather than
   * where it stood then, so the "authoritative" answer it produces is wrong in a
   * different way — and it is an order of magnitude more code to get subtly
   * wrong. Easing the error out over a tenth of a second is invisible at the
   * scale of disagreement a walking-speed game actually produces, and the hard
   * snap above `SNAP_ERROR_M` covers the case where it is not.
   */
  reconcile(p: NetPlayer): void {
    const body = this.body;
    if (!body) return;
    const dx = p.x - body.position.x;
    const dy = p.y - body.position.y;
    const dz = p.z - body.position.z;
    const err = Math.hypot(dx, dy, dz);
    if (err > SNAP_ERROR_M) {
      body.teleport({ x: p.x, y: p.y, z: p.z }, body.yaw);
      this.error.set(0, 0, 0);
      return;
    }
    this.error.set(dx, dy, dz);
    this.kg = p.kg;
  }

  /** Client: bleed the correction into the capsule. Called on the fixed step. */
  applyCorrection(dt: number): void {
    if (!this.body || this.error.lengthSq() < 1e-8) return;
    const k = Math.min(1, dt * CORRECT_RATE);
    this.body.position.x += this.error.x * k;
    this.body.position.y += this.error.y * k;
    this.body.position.z += this.error.z * k;
    this.error.multiplyScalar(1 - k);
  }

  /**
   * Pick the transform to draw with, then pose.
   *
   * Simulated actors read straight off their capsule. Interpolated ones are
   * played back `INTERP_MS` behind, and their speed is measured from the
   * playback rather than sent — a speed off the wire is one snapshot stale and
   * makes the legs skate a tenth of a second behind the body.
   */
  render(dt: number, now: number): void {
    this.prev.copy(this.pos);
    if (this.body) {
      this.pos.copy(this.body.position);
      this.yaw = this.body.yaw;
      this.pitch = this.body.pitch;
    } else if (this.buf.length) {
      this.sampleAt(now - INTERP_MS);
    }

    const fig = this.figure;
    if (!fig) return;
    const speed = dt > 0 ? Math.hypot(this.pos.x - this.prev.x, this.pos.z - this.prev.z) / dt : 0;
    // Smoothed, because a 20Hz playback divided by a 144Hz frame is a speed that
    // alternates between zero and double on consecutive frames, and a gait fed
    // that alternates between standing and sprinting.
    this.frame.speed += (speed - this.frame.speed) * Math.min(1, dt * 12);
    fig.root.position.copy(this.pos);
    fig.root.rotation.y = this.yaw;
    if (fig instanceof ThiefFigure) {
      fig.pose({
        speed: this.frame.speed,
        dt,
        // The body already faces the yaw, so the neck only has to carry the
        // pitch. A remote player's head turn IS their body turn.
        headYaw: 0,
        headPitch: this.pitch,
        crouch: this.frame.crouching ? 1 : 0,
        load: Math.min(1, this.kg / 30),
        working: this.frame.sprinting && this.frame.speed < 0.2 ? 1 : 0,
        down: this.down,
      });
    } else {
      fig.pose({ speed: this.frame.speed, dt, headYaw: 0, headPitch: this.pitch, down: this.down });
    }
  }

  /** Where the buffer says they were at `at` (a local ms timestamp). */
  private sampleAt(at: number): void {
    const b = this.buf;
    // Drop everything strictly older than the one bracketing `at`, so the buffer
    // does not grow and the search below is over two or three entries.
    while (b.length > 2 && b[1].t <= at) b.shift();
    if (b.length === 1 || at <= b[0].t) {
      this.set(b[0]);
      return;
    }
    const a = b[0];
    const c = b[1];
    const span = c.t - a.t;
    // Clamped rather than extrapolated. Guessing past the newest sample makes a
    // stalled player drift through a wall and then snap back, which reads as a
    // much worse connection than the honest freeze.
    const u = span > 1e-3 ? Math.max(0, Math.min(1, (at - a.t) / span)) : 1;
    this.pos.set(a.x + (c.x - a.x) * u, a.y + (c.y - a.y) * u, a.z + (c.z - a.z) * u);
    this.yaw = a.yaw + shortestTurn(a.yaw, c.yaw) * u;
    this.pitch = a.pitch + (c.pitch - a.pitch) * u;
    this.kg = c.kg;
    this.readFlags(c.flags);
  }

  private set(s: Sample): void {
    this.pos.set(s.x, s.y, s.z);
    this.yaw = s.yaw;
    this.pitch = s.pitch;
    this.kg = s.kg;
    this.readFlags(s.flags);
  }

  private readFlags(flags: number): void {
    this.frame.crouching = (flags & PFlag.Crouching) !== 0;
    this.frame.sprinting = (flags & PFlag.Sprinting) !== 0;
    this.frame.grounded = (flags & PFlag.Grounded) !== 0;
  }

  /** Bit-pack this actor's stance for the wire. */
  get flags(): number {
    return (
      (this.frame.crouching ? PFlag.Crouching : 0) |
      (this.frame.sprinting ? PFlag.Sprinting : 0) |
      (this.frame.grounded ? PFlag.Grounded : 0) |
      (this.team === 'guard' ? PFlag.Guard : 0)
    );
  }

  setVisible(v: boolean): void {
    if (this.figure) this.figure.root.visible = v;
  }

  dispose(): void {
    this.body?.dispose();
    this.body = null;
    if (this.figure instanceof ThiefFigure) this.figure.dispose();
    else this.figure?.root.removeFromParent();
    this.figure = null;
  }
}

/**
 * Everyone in the round, in slot order.
 *
 * Slot order matters: it is the order actors go onto the wire and the order the
 * armband colours are handed out, and both have to agree on every machine.
 */
export class Crew {
  private readonly byId = new Map<string, Actor>();
  private readonly bySlot: (Actor | undefined)[] = [];

  get all(): Actor[] {
    return [...this.byId.values()];
  }

  get size(): number {
    return this.byId.size;
  }

  get(id: string): Actor | undefined {
    return this.byId.get(id);
  }

  atSlot(slot: number): Actor | undefined {
    return this.bySlot[slot];
  }

  add(init: ActorInit): Actor {
    const existing = this.byId.get(init.id);
    if (existing) return existing;
    const a = new Actor(init);
    this.byId.set(a.id, a);
    this.bySlot[a.slot] = a;
    return a;
  }

  remove(id: string): void {
    const a = this.byId.get(id);
    if (!a) return;
    a.dispose();
    this.byId.delete(id);
    if (this.bySlot[a.slot] === a) this.bySlot[a.slot] = undefined;
  }

  clear(): void {
    for (const a of this.byId.values()) a.dispose();
    this.byId.clear();
    this.bySlot.length = 0;
  }

  /** The first free slot, or -1 if the lobby is full. */
  freeSlot(max: number): number {
    for (let i = 0; i < max; i++) if (!this.bySlot[i]) return i;
    return -1;
  }
}

// ---------------------------------------------------------------------------

function copyCmd(from: Command, to: Command): void {
  to.seq = from.seq;
  to.forward = from.forward;
  to.strafe = from.strafe;
  to.buttons = from.buttons;
  to.yaw = from.yaw;
  to.pitch = from.pitch;
}

/**
 * Is `a` newer than `b`, given both wrap at 16 bits?
 *
 * The naive `a > b` breaks once every 18 minutes at 60Hz, at which point every
 * subsequent command from that player is discarded as a duplicate and they
 * freeze for good. Comparing the signed distance costs nothing and does not.
 */
export function seqNewer(a: number, b: number): boolean {
  return ((a - b) & 0xffff) !== 0 && ((a - b) & 0xffff) < 0x8000;
}

/** Shortest signed angle from a to b, so a body never spins the long way. */
export function shortestTurn(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export const CREW_TUNING = { INTERP_MS, SNAP_ERROR_M, CORRECT_RATE, BUF_MS };
